import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

var API = 'https://surabaya-api.onrender.com/api';

// ============================================
// FOOD COST MODULE - Surabaya Café
// ============================================

function FoodCost() {
  var [token, setToken] = useState('');
  var [loggedIn, setLoggedIn] = useState(false);
  var [password, setPassword] = useState('');
  var [tab, setTab] = useState('dashboard');
  var [ingredients, setIngredients] = useState([]);
  var [composants, setComposants] = useState([]);
  var [plats, setPlats] = useState([]);
  var [dashboard, setDashboard] = useState(null);
  var [ventes, setVentes] = useState(null);
  var [periode, setPeriode] = useState(30);
  var [loading, setLoading] = useState(false);
  var [editItem, setEditItem] = useState(null);
  var [editType, setEditType] = useState('');
  var [searchTerm, setSearchTerm] = useState('');
  var [hiboutikProducts, setHiboutikProducts] = useState([]);
  var [editingMappings, setEditingMappings] = useState([]);
  var [hbSearch, setHbSearch] = useState('');
  var [hbSelected, setHbSelected] = useState(null);
  var [hbRatio, setHbRatio] = useState('');

  // Auth fetch
  var authFetch = useCallback(function(url, options) {
    var opts = options || {};
    var headers = Object.assign({}, opts.headers || {}, {
      'Authorization': 'Bearer ' + token,
      'Content-Type': 'application/json'
    });
    return fetch(url, Object.assign({}, opts, { headers: headers }));
  }, [token]);

  // Login
  function handleLogin(e) {
    e.preventDefault();
    fetch(API + '/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: password })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      if (data.token) {
        setToken(data.token);
        setLoggedIn(true);
      } else {
        alert('Mot de passe incorrect');
      }
    });
  }

  // Load data
  var loadData = useCallback(function() {
    if (!token) return;
    setLoading(true);
    Promise.all([
      authFetch(API + '/foodcost/ingredients').then(function(r) { return r.json(); }),
      authFetch(API + '/foodcost/composants').then(function(r) { return r.json(); }),
      authFetch(API + '/foodcost/plats').then(function(r) { return r.json(); }),
      authFetch(API + '/foodcost/dashboard').then(function(r) { return r.json(); }),
      authFetch(API + '/foodcost/ventes?date_start=' + new Date(Date.now()-periode*24*60*60*1000).toISOString().split('T')[0] + '&date_end=' + new Date().toISOString().split('T')[0]).then(function(r) { return r.json(); }),
      authFetch(API + '/foodcost/hiboutik-products').then(function(r) { return r.json(); }).catch(function() { return []; })
    ]).then(function(results) {
      setIngredients(results[0]);
      setComposants(results[1]);
      setPlats(results[2]);
      setDashboard(results[3]);
      setVentes(results[4]);
      setHiboutikProducts(Array.isArray(results[5]) ? results[5] : []);
      setLoading(false);
    }).catch(function(err) {
      console.error(err);
      setLoading(false);
    });
  }, [token, authFetch, periode]);

  useEffect(function() {
    if (loggedIn) loadData();
  }, [loggedIn, loadData]);

  // Save ingredient
  function saveIngredient(ing) {
    var method = ing.id ? 'PUT' : 'POST';
    var url = ing.id ? API + '/foodcost/ingredients/' + ing.id : API + '/foodcost/ingredients';
    authFetch(url, { method: method, body: JSON.stringify(ing) })
    .then(function(r) { return r.json(); })
    .then(function() { loadData(); setEditItem(null); });
  }

  // Charger les mappings Hiboutik quand on ouvre la modal d'un ingredient existant
  var editIngredientId = (editType === 'ingredient' && editItem && editItem.id) ? editItem.id : null;
  useEffect(function() {
    // Reset mini-form mapping a chaque ouverture/fermeture
    setHbSearch(''); setHbSelected(null); setHbRatio('');
    if (!editIngredientId) { setEditingMappings([]); return; }
    authFetch(API + '/foodcost/ingredients/' + editIngredientId + '/hiboutik-mappings')
      .then(function(r) { return r.json(); })
      .then(function(rows) { setEditingMappings(Array.isArray(rows) ? rows : []); })
      .catch(function() { setEditingMappings([]); });
  }, [editIngredientId, authFetch]);

  function addHiboutikMapping(ingredientId, hiboutikProductId, quantite, notes) {
    return authFetch(API + '/foodcost/ingredients/' + ingredientId + '/hiboutik-mappings', {
      method: 'POST',
      body: JSON.stringify({ hiboutik_product_id: hiboutikProductId, quantite: quantite, notes: notes || '' })
    })
    .then(function(r) { return r.json().then(function(data) { return { ok: r.ok, data: data }; }); })
    .then(function(res) {
      if (!res.ok) {
        alert('Erreur ajout mapping : ' + (res.data.error || 'inconnue'));
        return null;
      }
      // Recharger les mappings
      authFetch(API + '/foodcost/ingredients/' + ingredientId + '/hiboutik-mappings')
        .then(function(r) { return r.json(); })
        .then(function(rows) { setEditingMappings(Array.isArray(rows) ? rows : []); });
      return res.data;
    });
  }

  function deleteHiboutikMapping(mappingId, ingredientId) {
    if (!window.confirm('Supprimer ce mapping Hiboutik ?')) return;
    authFetch(API + '/foodcost/hiboutik-mappings/' + mappingId, { method: 'DELETE' })
      .then(function(r) { return r.json(); })
      .then(function() {
        // Recharger
        authFetch(API + '/foodcost/ingredients/' + ingredientId + '/hiboutik-mappings')
          .then(function(r) { return r.json(); })
          .then(function(rows) { setEditingMappings(Array.isArray(rows) ? rows : []); });
      });
  }

  // Save composant
  function saveComposant(comp) {
    var method = comp.id ? 'PUT' : 'POST';
    var url = comp.id ? API + '/foodcost/composants/' + comp.id : API + '/foodcost/composants';
    authFetch(url, { method: method, body: JSON.stringify(comp) })
    .then(function(r) { return r.json(); })
    .then(function() { loadData(); setEditItem(null); });
  }

  // Save plat
  function savePlat(plat) {
    var method = plat.id ? 'PUT' : 'POST';
    var url = plat.id ? API + '/foodcost/plats/' + plat.id : API + '/foodcost/plats';
    authFetch(url, { method: method, body: JSON.stringify(plat) })
    .then(function(r) { return r.json(); })
    .then(function() { loadData(); setEditItem(null); });
  }

  // Delete
  function deleteIngredient(id) {
    if (!window.confirm('Supprimer cet ingrédient ?')) return;
    authFetch(API + '/foodcost/ingredients/' + id, { method: 'DELETE' }).then(function() { loadData(); });
  }
  function deleteComposant(id) {
    if (!window.confirm('Supprimer ce composant ?')) return;
    authFetch(API + '/foodcost/composants/' + id, { method: 'DELETE' }).then(function() { loadData(); });
  }
  function deletePlat(id) {
    if (!window.confirm('Supprimer ce plat ?')) return;
    authFetch(API + '/foodcost/plats/' + id, { method: 'DELETE' }).then(function() { loadData(); });
  }

  // Recalc all
  function recalcAll() {
    setLoading(true);
    authFetch(API + '/foodcost/recalc', { method: 'POST' })
    .then(function() { loadData(); });
  }

  // ============================================
  // LOGIN SCREEN
  // ============================================
  if (!loggedIn) {
    return React.createElement('div', { style: styles.loginContainer },
      React.createElement('div', { style: styles.loginCard },
        React.createElement('h1', { style: styles.loginTitle }, '🏔️ Surabaya'),
        React.createElement('h2', { style: styles.loginSubtitle }, 'Food Cost Manager'),
        React.createElement('form', { onSubmit: handleLogin },
          React.createElement('input', {
            type: 'password',
            placeholder: 'Mot de passe admin',
            value: password,
            onChange: function(e) { setPassword(e.target.value); },
            style: styles.input
          }),
          React.createElement('button', { type: 'submit', style: styles.btnPrimary }, 'Connexion')
        )
      )
    );
  }

  // ============================================
  // MAIN LAYOUT
  // ============================================
  return React.createElement('div', { style: styles.app },
    // Header
    React.createElement('div', { style: styles.header },
      React.createElement('h1', { style: styles.headerTitle }, '🏔️ Surabaya — Food Cost'),
      React.createElement('div', { style: styles.headerActions },
        React.createElement('button', { onClick: recalcAll, style: styles.btnSmall }, '🔄 Recalculer'),
        loading && React.createElement('span', { style: { color: '#f59e0b', marginLeft: 10 } }, '⏳ Chargement...')
      )
    ),
    // Tabs
    React.createElement('div', { style: styles.tabs },
      ['dashboard', 'plats', 'composants', 'ingredients', 'commande'].map(function(t) {
        var labels = { dashboard: '📊 Dashboard', plats: '🍽️ Plats', composants: '🥘 Composants', ingredients: '📦 Mercuriale', commande: '🛒 Bon de commande' };
        return React.createElement('button', {
          key: t,
          onClick: function() { setTab(t); setEditItem(null); },
          style: Object.assign({}, styles.tab, tab === t ? styles.tabActive : {})
        }, labels[t]);
      })
    ),
    // Search bar (for non-dashboard, non-commande tabs)
    (tab !== 'dashboard' && tab !== 'commande') && React.createElement('div', { style: styles.searchBar },
      React.createElement('input', {
        placeholder: 'Rechercher...',
        value: searchTerm,
        onChange: function(e) { setSearchTerm(e.target.value); },
        style: styles.searchInput
      })
    ),
    // Content
    React.createElement('div', { style: styles.content },
      tab === 'dashboard' && renderDashboard(dashboard, plats, ventes, periode, setPeriode),
      tab === 'ingredients' && renderIngredients(ingredients, searchTerm, editItem, editType, setEditItem, setEditType, saveIngredient, deleteIngredient, hiboutikProducts, editingMappings, addHiboutikMapping, deleteHiboutikMapping, hbSearch, setHbSearch, hbSelected, setHbSelected, hbRatio, setHbRatio),
      tab === 'composants' && renderComposants(composants, ingredients, searchTerm, editItem, editType, setEditItem, setEditType, saveComposant, deleteComposant),
      tab === 'plats' && renderPlats(plats, ingredients, composants, searchTerm, editItem, editType, setEditItem, setEditType, savePlat, deletePlat),
      tab === 'commande' && React.createElement(BonCommande, { plats: plats, ingredients: ingredients, composants: composants })
    )
  );
}

// ============================================
// DASHBOARD
// ============================================
function renderDashboard(dashboard, plats, ventes, periode, setPeriode) {
  if (!dashboard) return React.createElement('p', null, 'Chargement...');

  return React.createElement('div', null,
    React.createElement('div', { style: styles.kpiRow },
      kpiCard('🍽️ Plats actifs', dashboard.totalPlats, ''),
      kpiCard('📊 Ratio moyen', dashboard.avgRatio, '%'),
      kpiCard('💰 Marge moyenne', dashboard.avgMargin, '€'),
      kpiCard('⚠️ Alertes (>35%)', dashboard.alertPlats ? dashboard.alertPlats.length : 0, '')
    ),
    dashboard.byCategory && React.createElement('div', { style: styles.section },
      React.createElement('h3', { style: styles.sectionTitle }, 'Par catégorie'),
      React.createElement('div', { style: styles.kpiRow },
        Object.keys(dashboard.byCategory).map(function(cat) {
          var d = dashboard.byCategory[cat];
          return kpiCard(cat, d.avgRatio, '% (' + d.count + ' plats)');
        })
      )
    ),
    React.createElement('div', { style: styles.section },
      React.createElement('h3', { style: styles.sectionTitle }, 'Tous les plats'),
      React.createElement('table', { style: styles.table },
        React.createElement('thead', null,
          React.createElement('tr', null,
            ['Plat', 'Catégorie', 'Coût', 'Prix vente', 'Ratio', 'Marge'].map(function(h) {
              return React.createElement('th', { key: h, style: styles.th }, h);
            })
          )
        ),
        React.createElement('tbody', null,
          (dashboard.plats || []).map(function(p) {
            var ratio = parseFloat(p.ratio_percent) || 0;
            var ratioColor = ratio > 35 ? '#ef4444' : ratio > 30 ? '#f59e0b' : ratio > 25 ? '#3b82f6' : '#22c55e';
            return React.createElement('tr', { key: p.id, style: styles.tr },
              React.createElement('td', { style: styles.td }, React.createElement('strong', null, p.name)),
              React.createElement('td', { style: styles.td }, p.category),
              React.createElement('td', { style: styles.tdRight }, parseFloat(p.food_cost_per_portion).toFixed(2) + '€'),
              React.createElement('td', { style: styles.tdRight }, parseFloat(p.sell_price).toFixed(2) + '€'),
              React.createElement('td', { style: Object.assign({}, styles.tdRight, { color: ratioColor, fontWeight: 700 }) },
                ratio.toFixed(1) + '%'
              ),
              React.createElement('td', { style: styles.tdRight }, parseFloat(p.margin).toFixed(2) + '€')
            );
          })
        )
      )
    )
    ,
    React.createElement('div', { style: styles.section },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 } },
        React.createElement('h3', { style: Object.assign({}, styles.sectionTitle, { margin: 0 }) }, '📈 Ventes Hiboutik'),
        ['30', '90', '365'].map(function(p) {
          return React.createElement('button', {
            key: p,
            onClick: function() { setPeriode(parseInt(p)); },
            style: { padding: '4px 12px', borderRadius: 6, border: '1px solid #ccc', cursor: 'pointer',
              background: periode === parseInt(p) ? '#92400e' : '#fff',
              color: periode === parseInt(p) ? '#fff' : '#333', fontWeight: periode === parseInt(p) ? 700 : 400 }
          }, p === '30' ? '30 jours' : p === '90' ? '3 mois' : '1 an');
        })
      ),
      ventes && ventes.parMois && ventes.parMois.length > 0
        ? React.createElement(BarChart, { width: 700, height: 250, data: ventes.parMois, style: { margin: '0 auto' } },
            React.createElement(CartesianGrid, { strokeDasharray: '3 3' }),
            React.createElement(XAxis, { dataKey: 'mois' }),
            React.createElement(YAxis, null),
            React.createElement(Tooltip, null),
            React.createElement(Bar, { dataKey: 'ca', fill: '#92400e', name: 'CA (€)' })
          )
        : React.createElement('p', null, 'Chargement graphique...')
    ),
    React.createElement('div', { style: styles.section },
      React.createElement('h3', { style: styles.sectionTitle }, '🏆 Top ventes'),
      ventes && ventes.ventes && ventes.ventes.length > 0
        ? React.createElement('table', { style: styles.table },
            React.createElement('thead', null,
              React.createElement('tr', null,
                ['Plat', 'Catégorie', 'Qté vendue', 'CA total'].map(function(h) {
                  return React.createElement('th', { key: h, style: styles.th }, h);
                })
              )
            ),
            React.createElement('tbody', null,
              ventes.ventes.filter(function(v) { return v.product_name; }).slice(0, 10).map(function(v) {
                return React.createElement('tr', { key: v.product_id, style: styles.tr },
                  React.createElement('td', { style: styles.td }, React.createElement('strong', null, v.product_name)),
                  React.createElement('td', { style: styles.td }, v.category || '-'),
                  React.createElement('td', { style: styles.tdRight }, v.quantite_vendue),
                  React.createElement('td', { style: styles.tdRight }, v.ca_total.toFixed(2) + '€')
                );
              })
            )
          )
        : React.createElement('p', null, ventes ? 'Aucune vente trouvée' : 'Chargement...')
    )
  );
}

function kpiCard(title, value, suffix) {
  return React.createElement('div', { key: title, style: styles.kpiCard },
    React.createElement('div', { style: styles.kpiValue }, value + suffix),
    React.createElement('div', { style: styles.kpiLabel }, title)
  );
}

// ============================================
// BON DE COMMANDE
// ============================================
function BonCommande({ plats, ingredients, composants }) {
  var [couverts, setCouverts] = useState(20);
  var [selection, setSelection] = useState({});

  // Init selection avec tous les plats cochés
  useEffect(function() {
    var init = {};
    plats.forEach(function(p) { init[p.id] = true; });
    setSelection(init);
  }, [plats]);

  // Calcul des besoins
  var besoins = {};

  plats.forEach(function(plat) {
    if (!selection[plat.id]) return;

    // Ingrédients directs
    (plat.ingredients || []).forEach(function(ing) {
      var key = ing.ingredient_id || ing.id;
      var ingData = ingredients.find(function(i) { return i.id === key; });
      if (!ingData) return;
      var qteTotal = (parseFloat(ing.quantity) || 0) * couverts;
      if (!besoins[key]) besoins[key] = { name: ingData.name, unit: ingData.unit, category: ingData.category || 'Autre', qte: 0, cout: 0 };
      besoins[key].qte += qteTotal;
      besoins[key].cout += qteTotal * (parseFloat(ingData.net_cost) || 0);
    });

    // Via composants
    (plat.composants || []).forEach(function(comp) {
      var compData = composants.find(function(c) { return c.id === (comp.composant_id || comp.id); });
      if (!compData) return;
      var nbPortions = (parseFloat(comp.quantity) || 1) * couverts;
      (compData.ingredients || []).forEach(function(ing) {
        var ingData = ingredients.find(function(i) { return i.id === ing.ingredient_id; });
        if (!ingData) return;
        var qteTotal = (parseFloat(ing.quantity) || 0) * nbPortions / (parseFloat(compData.portions_count) || 1);
        if (!besoins[ingData.id]) besoins[ingData.id] = { name: ingData.name, unit: ingData.unit, category: ingData.category || 'Autre', qte: 0, cout: 0 };
        besoins[ingData.id].qte += qteTotal;
        besoins[ingData.id].cout += qteTotal * (parseFloat(ingData.net_cost) || 0);
      });
    });
  });

  // Grouper par catégorie
  var parCat = {};
  Object.values(besoins).forEach(function(b) {
    if (!parCat[b.category]) parCat[b.category] = [];
    parCat[b.category].push(b);
  });

  var totalCout = Object.values(besoins).reduce(function(acc, b) { return acc + b.cout; }, 0);
  var nbPlatsSelec = Object.values(selection).filter(Boolean).length;

  return React.createElement('div', null,
    // Controls
    React.createElement('div', { style: { display: 'flex', gap: 20, alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap' } },
      React.createElement('div', null,
        React.createElement('label', { style: styles.label }, 'Nombre de couverts'),
        React.createElement('input', {
          type: 'number', min: 1, max: 200,
          value: couverts,
          onChange: function(e) { setCouverts(parseInt(e.target.value) || 1); },
          style: Object.assign({}, styles.input, { width: 100 })
        })
      ),
      React.createElement('div', { style: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 16px' } },
        React.createElement('strong', null, nbPlatsSelec + ' plats sélectionnés'),
        React.createElement('span', { style: { color: '#64748b', marginLeft: 10 } }, '→ Coût matière estimé : '),
        React.createElement('strong', { style: { color: '#2563eb', fontSize: 18 } }, totalCout.toFixed(2) + '€')
      )
    ),

    // Sélection des plats
    React.createElement('div', { style: { marginBottom: 20 } },
      React.createElement('h4', { style: styles.sectionTitle }, 'Plats au menu ce service'),
      React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 8 } },
        plats.map(function(p) {
          var sel = !!selection[p.id];
          return React.createElement('button', {
            key: p.id,
            onClick: function() {
              var s = Object.assign({}, selection);
              s[p.id] = !s[p.id];
              setSelection(s);
            },
            style: Object.assign({}, styles.btnSmall, {
              background: sel ? '#2563eb' : '#f1f5f9',
              color: sel ? 'white' : '#475569',
              border: sel ? '1px solid #2563eb' : '1px solid #d1d5db'
            })
          }, p.name);
        })
      )
    ),

    // Résultat par catégorie
    Object.keys(parCat).sort().map(function(cat) {
      var items = parCat[cat].sort(function(a, b) { return a.name.localeCompare(b.name); });
      var coutCat = items.reduce(function(acc, i) { return acc + i.cout; }, 0);
      return React.createElement('div', { key: cat, style: { marginBottom: 20 } },
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #e2e8f0', paddingBottom: 6, marginBottom: 8 } },
          React.createElement('h4', { style: { margin: 0, color: '#1e293b' } }, cat),
          React.createElement('span', { style: { color: '#64748b', fontSize: 13 } }, coutCat.toFixed(2) + '€')
        ),
        React.createElement('table', { style: styles.table },
          React.createElement('thead', null,
            React.createElement('tr', null,
              ['Ingrédient', 'Quantité', 'Unité', 'Coût'].map(function(h) {
                return React.createElement('th', { key: h, style: styles.th }, h);
              })
            )
          ),
          React.createElement('tbody', null,
            items.map(function(item, i) {
              return React.createElement('tr', { key: i, style: styles.tr },
                React.createElement('td', { style: styles.td }, React.createElement('strong', null, item.name)),
                React.createElement('td', { style: styles.tdRight }, item.qte.toFixed(3)),
                React.createElement('td', { style: styles.td }, item.unit),
                React.createElement('td', { style: styles.tdRight }, item.cout.toFixed(2) + '€')
              );
            })
          )
        )
      );
    }),

    Object.keys(besoins).length === 0 && React.createElement('div', { style: { textAlign: 'center', color: '#94a3b8', padding: 40 } },
      'Sélectionne des plats et renseigne les fiches recettes pour voir les besoins 👆'
    )
  );
}

// ============================================
// INGREDIENTS (Mercuriale)
// ============================================
function renderIngredients(ingredients, searchTerm, editItem, editType, setEditItem, setEditType, saveIngredient, deleteIngredient, hiboutikProducts, editingMappings, addHiboutikMapping, deleteHiboutikMapping, hbSearch, setHbSearch, hbSelected, setHbSelected, hbRatio, setHbRatio) {
  var filtered = ingredients.filter(function(i) {
    if (!searchTerm) return true;
    var s = searchTerm.toLowerCase();
    return (i.name || '').toLowerCase().includes(s) || (i.code || '').toLowerCase().includes(s) || (i.category || '').toLowerCase().includes(s);
  });

  var categories = {};
  filtered.forEach(function(i) {
    var cat = i.category || 'Non classé';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(i);
  });

  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 } },
      React.createElement('h3', { style: styles.sectionTitle }, filtered.length + ' ingrédients'),
      React.createElement('button', {
        onClick: function() { setEditItem({ code: 'ING-NEW', name: '', category: '', unit: 'kg', selected_price: 0, yield_percent: 100 }); setEditType('ingredient'); },
        style: styles.btnPrimary
      }, '+ Ajouter')
    ),
    editType === 'ingredient' && editItem && renderIngredientForm(editItem, setEditItem, saveIngredient, hiboutikProducts || [], editingMappings || [], addHiboutikMapping, deleteHiboutikMapping, hbSearch, setHbSearch, hbSelected, setHbSelected, hbRatio, setHbRatio),
    Object.keys(categories).sort().map(function(cat) {
      return React.createElement('div', { key: cat, style: { marginBottom: 20 } },
        React.createElement('h4', { style: { color: '#64748b', fontSize: 14, marginBottom: 8, borderBottom: '1px solid #e2e8f0', paddingBottom: 4 } }, cat + ' (' + categories[cat].length + ')'),
        React.createElement('table', { style: styles.table },
          React.createElement('thead', null,
            React.createElement('tr', null,
              ['Code', 'Ingrédient', 'Unité', 'Prix', 'Rdt%', 'Coût net', 'Allergènes', ''].map(function(h) {
                return React.createElement('th', { key: h, style: styles.th }, h);
              })
            )
          ),
          React.createElement('tbody', null,
            categories[cat].map(function(i) {
              return React.createElement('tr', { key: i.id, style: styles.tr },
                React.createElement('td', { style: Object.assign({}, styles.td, { fontFamily: 'monospace', fontSize: 12 }) }, i.code),
                React.createElement('td', { style: styles.td }, i.name),
                React.createElement('td', { style: styles.td }, i.unit),
                React.createElement('td', { style: styles.tdRight }, parseFloat(i.selected_price).toFixed(2) + '€'),
                React.createElement('td', { style: styles.tdRight }, parseFloat(i.yield_percent).toFixed(0) + '%'),
                React.createElement('td', { style: Object.assign({}, styles.tdRight, { fontWeight: 600 }) }, parseFloat(i.net_cost).toFixed(4) + '€'),
                React.createElement('td', { style: Object.assign({}, styles.td, { fontSize: 11, color: '#ef4444' }) }, i.allergens || ''),
                React.createElement('td', { style: Object.assign({}, styles.td, { display: 'flex', gap: 4 }) },
                  React.createElement('button', {
                    onClick: function() { setEditItem(Object.assign({}, i)); setEditType('ingredient'); },
                    style: styles.btnEdit
                  }, '✏️'),
                  React.createElement('button', {
                    onClick: function() { deleteIngredient(i.id); },
                    style: styles.btnEdit
                  }, '🗑️')
                )
              );
            })
          )
        )
      );
    })
  );
}

function renderIngredientForm(item, setEditItem, save, hiboutikProducts, editingMappings, addHiboutikMapping, deleteHiboutikMapping, hbSearch, setHbSearch, hbSelected, setHbSelected, hbRatio, setHbRatio) {
  function tryAddMapping() {
    if (!hbSelected) { alert('Choisis un produit Hiboutik dans la liste.'); return; }
    var ratio = parseFloat(hbRatio);
    if (!isFinite(ratio) || ratio <= 0) { alert('Le ratio doit etre > 0.'); return; }
    addHiboutikMapping(item.id, hbSelected.id, ratio, hbSelected.name + ' (cmd ' + ratio + ' ' + (item.unit || 'u') + ')').then(function(res) {
      if (res) { setHbSearch(''); setHbSelected(null); setHbRatio(''); }
    });
  }

  // Filtrage local de la liste Hiboutik
  var hbFiltered = (hiboutikProducts || []).filter(function(p) {
    if (!hbSearch || hbSearch.length < 2) return false;
    var s = hbSearch.toLowerCase();
    return (p.name || '').toLowerCase().includes(s) || String(p.id).indexOf(s) >= 0;
  }).slice(0, 8);

  function upd(field, value) {
    var updated = Object.assign({}, item);
    updated[field] = value;
    if (field === 'selected_price' || field === 'yield_percent') {
      var price = field === 'selected_price' ? parseFloat(value) || 0 : parseFloat(updated.selected_price) || 0;
      var yld = field === 'yield_percent' ? parseFloat(value) || 100 : parseFloat(updated.yield_percent) || 100;
      updated.net_cost = yld > 0 ? price / (yld / 100) : price;
    }
    setEditItem(updated);
  }

  return React.createElement('div', { style: styles.modal },
    React.createElement('div', { style: styles.modalContent },
      React.createElement('h3', null, item.id ? 'Modifier ingrédient' : 'Nouvel ingrédient'),
      formRow('Code', 'code', item.code, function(v) { upd('code', v); }),
      formRow('Nom', 'name', item.name, function(v) { upd('name', v); }),
      formRow('Catégorie', 'category', item.category, function(v) { upd('category', v); }),
      formRow('Unité', 'unit', item.unit, function(v) { upd('unit', v); }),
      formRow('Prix retenu (€/unité)', 'selected_price', item.selected_price, function(v) { upd('selected_price', v); }, 'number'),
      formRow('Rendement (%)', 'yield_percent', item.yield_percent, function(v) { upd('yield_percent', v); }, 'number'),
      React.createElement('div', { style: { background: '#f0fdf4', padding: 10, borderRadius: 8, marginTop: 10, textAlign: 'center' } },
        React.createElement('strong', null, 'Coût net : ' + (parseFloat(item.net_cost) || 0).toFixed(4) + '€/' + (item.unit || 'unité'))
      ),
      formRow('Allergènes', 'allergens', item.allergens, function(v) { upd('allergens', v); }),

      // ============== GESTION STOCK PHYSIQUE ==============
      React.createElement('div', {
        style: {
          marginTop: 18, padding: 12, background: '#f8fafc',
          border: '1px solid #e2e8f0', borderRadius: 8
        }
      },
        React.createElement('label', {
          style: { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13, color: '#1e293b', cursor: 'pointer' }
        },
          React.createElement('input', {
            type: 'checkbox',
            checked: !!item.gestion_stock,
            onChange: function(e) { upd('gestion_stock', e.target.checked); }
          }),
          '📦 Activer la gestion stock physique'
        ),

        item.gestion_stock && React.createElement('div', { style: { marginTop: 12 } },
          // Fournisseur actif (radios)
          React.createElement('div', { style: { marginBottom: 10 } },
            React.createElement('label', { style: styles.label }, 'Fournisseur retenu pour les commandes'),
            React.createElement('div', { style: { display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' } },
              [1, 2, 3].map(function(n) {
                var supplierName = item['supplier' + n + '_name'] || '(non defini)';
                return React.createElement('label', {
                  key: 'fa-' + n,
                  style: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }
                },
                  React.createElement('input', {
                    type: 'radio',
                    name: 'fournisseur_actif',
                    checked: parseInt(item.fournisseur_actif, 10) === n,
                    onChange: function() { upd('fournisseur_actif', n); }
                  }),
                  'F' + n + ' : ' + supplierName
                );
              })
            )
          ),

          // Ligne stock actuel + seuils
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 } },
            formRow('Stock actuel (' + (item.unit || 'u') + ')', 'stock_actuel', item.stock_actuel, function(v) { upd('stock_actuel', v); }, 'number'),
            formRow('Seuil alerte été', 'seuil_alerte', item.seuil_alerte, function(v) { upd('seuil_alerte', v); }, 'number'),
            formRow('Seuil alerte hiver', 'seuil_alerte_hiver', item.seuil_alerte_hiver, function(v) { upd('seuil_alerte_hiver', v); }, 'number')
          ),

          // Ligne reappro
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 } },
            formRow('Quantité à commander', 'quantite_reappro', item.quantite_reappro, function(v) { upd('quantite_reappro', v); }, 'number'),
            formRow('Unité de commande', 'unite_commande', item.unite_commande, function(v) { upd('unite_commande', v); }),
            formRow('Facteur (1 cmd = X ' + (item.unit || 'u') + ')', 'facteur_commande', item.facteur_commande, function(v) { upd('facteur_commande', v); }, 'number')
          ),

          React.createElement('div', { style: { fontSize: 11, color: '#64748b', marginTop: 6, fontStyle: 'italic' } },
            'Ex : si vous commandez en sacs de 25kg pour un ingredient stocke en kg, unite=\'sac 25kg\' et facteur=25.'
          ),

          // ============== UI MAPPING HIBOUTIK ==============
          item.id && React.createElement('div', {
            style: { marginTop: 16, paddingTop: 12, borderTop: '1px dashed #cbd5e1' }
          },
            React.createElement('div', { style: { fontWeight: 600, fontSize: 13, color: '#1e293b', marginBottom: 6 } },
              '🔗 Vendu directement en caisse Hiboutik'
            ),
            React.createElement('div', { style: { fontSize: 11, color: '#64748b', marginBottom: 8, fontStyle: 'italic' } },
              'Quand un produit Hiboutik mappe ici se vend, le stock baisse automatiquement.'
            ),

            // Liste des mappings actuels
            (editingMappings || []).length > 0 && React.createElement('div', { style: { marginBottom: 10 } },
              React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 } }, 'Mappings actifs :'),
              (editingMappings || []).map(function(m) {
                var product = (hiboutikProducts || []).find(function(p) { return p.id === m.hiboutik_product_id; });
                var label = product ? '#' + product.id + ' ' + product.name + ' (' + product.price.toFixed(2) + '€)' : 'Hiboutik #' + m.hiboutik_product_id;
                return React.createElement('div', {
                  key: m.id,
                  style: { display: 'flex', alignItems: 'center', gap: 8, padding: 6, background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 4, fontSize: 12 }
                },
                  React.createElement('span', { style: { flex: 1 } }, label),
                  React.createElement('span', { style: { color: '#64748b' } }, '1 vente = -' + parseFloat(m.quantite).toFixed(3) + ' ' + (item.unit || 'u')),
                  React.createElement('button', {
                    onClick: function() { deleteHiboutikMapping(m.id, item.id); },
                    style: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '2px 6px' },
                    title: 'Supprimer ce mapping'
                  }, '🗑️')
                );
              })
            ),

            // Form ajout
            React.createElement('div', {
              style: { background: 'white', padding: 8, border: '1px solid #e2e8f0', borderRadius: 6 }
            },
              React.createElement('div', { style: { fontSize: 11, color: '#475569', marginBottom: 4, fontWeight: 600 } }, '+ Ajouter un mapping'),

              // Input recherche
              React.createElement('input', {
                type: 'text',
                placeholder: 'Tapez 2+ lettres pour chercher un produit Hiboutik...',
                value: hbSearch,
                onChange: function(e) { setHbSearch(e.target.value); setHbSelected(null); },
                style: Object.assign({}, styles.input, { fontSize: 12 })
              }),

              // Selection en cours
              hbSelected && React.createElement('div', {
                style: { fontSize: 12, color: '#16a34a', marginTop: 4, padding: 4, background: '#f0fdf4', borderRadius: 4 }
              }, '✓ ' + '#' + hbSelected.id + ' ' + hbSelected.name + ' (' + hbSelected.price.toFixed(2) + '€)'),

              // Resultats filtrage
              !hbSelected && hbFiltered.length > 0 && React.createElement('div', {
                style: { marginTop: 4, maxHeight: 150, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 4 }
              },
                hbFiltered.map(function(p) {
                  return React.createElement('div', {
                    key: p.id,
                    onClick: function() { setHbSelected(p); setHbSearch(p.name); },
                    style: { padding: '4px 6px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #f1f5f9' },
                    onMouseEnter: function(e) { e.target.style.background = '#f8fafc'; },
                    onMouseLeave: function(e) { e.target.style.background = 'white'; }
                  }, '#' + p.id + ' ' + p.name + ' (' + p.price.toFixed(2) + '€)');
                })
              ),

              // Ratio + bouton
              React.createElement('div', {
                style: { display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }
              },
                React.createElement('label', { style: { fontSize: 11, color: '#475569' } }, 'Ratio (1 vente = X ' + (item.unit || 'u') + ') :'),
                React.createElement('input', {
                  type: 'number',
                  step: '0.001',
                  placeholder: '0.4',
                  value: hbRatio,
                  onChange: function(e) { setHbRatio(e.target.value); },
                  style: { width: 80, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12 }
                }),
                React.createElement('button', {
                  onClick: tryAddMapping,
                  style: Object.assign({}, styles.btnSmall, { fontSize: 12, padding: '4px 12px' })
                }, '+ Ajouter')
              )
            )
          ),
          // Cas ou ingredient pas encore enregistre
          !item.id && React.createElement('div', {
            style: { marginTop: 12, padding: 8, background: '#fef3c7', borderRadius: 6, fontSize: 12, color: '#78350f' }
          }, 'ℹ️ Enregistre cet ingredient une premiere fois pour pouvoir lui ajouter un mapping Hiboutik.')

          // ============== FIN UI MAPPING HIBOUTIK ==============
        )
      ),
      // ============== FIN GESTION STOCK ==============

      React.createElement('div', { style: { display: 'flex', gap: 10, marginTop: 15 } },
        React.createElement('button', { onClick: function() {
          // Validation : si gestion_stock active, seuil_alerte et unite_commande requis
          if (item.gestion_stock) {
            if (!item.seuil_alerte || parseFloat(item.seuil_alerte) <= 0) {
              alert('⚠️ Le seuil d\'alerte été est requis quand la gestion stock est active.');
              return;
            }
            if (!item.unite_commande || !String(item.unite_commande).trim()) {
              alert('⚠️ L\'unité de commande est requise.');
              return;
            }
            if (!item.facteur_commande || parseFloat(item.facteur_commande) <= 0) {
              alert('⚠️ Le facteur de commande doit etre superieur a 0.');
              return;
            }
            if (!item.fournisseur_actif) {
              alert('⚠️ Selectionnez un fournisseur retenu.');
              return;
            }
          }
          save(item);
        }, style: styles.btnPrimary }, '💾 Enregistrer'),
        React.createElement('button', { onClick: function() { setEditItem(null); }, style: styles.btnCancel }, 'Annuler')
      )
    )
  );
}

// ============================================
// COMPOSANTS
// ============================================
function renderComposants(composants, ingredients, searchTerm, editItem, editType, setEditItem, setEditType, saveComposant, deleteComposant) {
  var filtered = composants.filter(function(c) {
    if (!searchTerm) return true;
    return (c.name || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 } },
      React.createElement('h3', { style: styles.sectionTitle }, filtered.length + ' composants'),
      React.createElement('button', {
        onClick: function() {
          setEditItem({ code: 'COMP-NEW', name: '', portions_count: 10, instructions: '', ingredients: [] });
          setEditType('composant');
        },
        style: styles.btnPrimary
      }, '+ Ajouter')
    ),
    editType === 'composant' && editItem && renderComposantForm(editItem, ingredients, setEditItem, saveComposant),
    React.createElement('div', { style: styles.cardGrid },
      filtered.map(function(c) {
        return React.createElement('div', { key: c.id, style: styles.card },
          React.createElement('div', { style: styles.cardHeader },
            React.createElement('strong', null, c.name),
            React.createElement('div', { style: { display: 'flex', gap: 4 } },
              React.createElement('button', {
                onClick: function() { setEditItem(Object.assign({}, c)); setEditType('composant'); },
                style: styles.btnEdit
              }, '✏️'),
              React.createElement('button', {
                onClick: function() { deleteComposant(c.id); },
                style: styles.btnEdit
              }, '🗑️')
            )
          ),
          React.createElement('div', { style: styles.cardBody },
            React.createElement('div', null, 'Portions : ' + c.portions_count),
            React.createElement('div', { style: { fontWeight: 700, color: '#2563eb', fontSize: 18 } },
              parseFloat(c.cost_per_portion).toFixed(2) + '€/portion'
            ),
            c.ingredients && c.ingredients.length > 0 && React.createElement('div', { style: { marginTop: 8, fontSize: 12, color: '#64748b' } },
              c.ingredients.map(function(i) {
                return React.createElement('div', { key: i.id }, '• ' + i.ingredient_name + ' : ' + i.quantity + ' ' + (i.unit || '') + ' → ' + parseFloat(i.cost).toFixed(2) + '€');
              })
            )
          )
        );
      })
    )
  );
}

function renderComposantForm(item, allIngredients, setEditItem, save) {
  function upd(field, value) {
    var updated = Object.assign({}, item);
    updated[field] = value;
    setEditItem(updated);
  }

  function addIngredient() {
    var ings = (item.ingredients || []).slice();
    ings.push({ ingredient_id: '', quantity: 0 });
    upd('ingredients', ings);
  }

  function updateIngLine(index, field, value) {
    var ings = (item.ingredients || []).slice();
    ings[index] = Object.assign({}, ings[index]);
    ings[index][field] = value;
    upd('ingredients', ings);
  }

  function removeIngLine(index) {
    var ings = (item.ingredients || []).filter(function(_, i) { return i !== index; });
    upd('ingredients', ings);
  }

  return React.createElement('div', { style: styles.modal },
    React.createElement('div', { style: styles.modalContent },
      React.createElement('h3', null, item.id ? 'Modifier composant' : 'Nouveau composant'),
      formRow('Code', 'code', item.code, function(v) { upd('code', v); }),
      formRow('Nom', 'name', item.name, function(v) { upd('name', v); }),
      formRow('Nb portions', 'portions_count', item.portions_count, function(v) { upd('portions_count', parseInt(v) || 1); }, 'number'),
      React.createElement('h4', { style: { marginTop: 15 } }, 'Ingrédients'),
      (item.ingredients || []).map(function(ing, idx) {
        return React.createElement('div', { key: idx, style: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 } },
          React.createElement('select', {
            value: ing.ingredient_id || '',
            onChange: function(e) { updateIngLine(idx, 'ingredient_id', parseInt(e.target.value)); },
            style: Object.assign({}, styles.input, { flex: 2 })
          },
            React.createElement('option', { value: '' }, '-- Choisir --'),
            allIngredients.map(function(i) {
              return React.createElement('option', { key: i.id, value: i.id }, i.name + ' (' + i.unit + ')');
            })
          ),
          React.createElement('input', {
            type: 'number', step: '0.001',
            value: ing.quantity,
            onChange: function(e) { updateIngLine(idx, 'quantity', parseFloat(e.target.value) || 0); },
            placeholder: 'Qté',
            style: Object.assign({}, styles.input, { flex: 1 })
          }),
          React.createElement('button', { onClick: function() { removeIngLine(idx); }, style: styles.btnDelete }, '🗑️')
        );
      }),
      React.createElement('button', { onClick: addIngredient, style: Object.assign({}, styles.btnSmall, { marginTop: 5 }) }, '+ Ingrédient'),
      React.createElement('div', { style: { display: 'flex', gap: 10, marginTop: 15 } },
        React.createElement('button', { onClick: function() { save(item); }, style: styles.btnPrimary }, '💾 Enregistrer'),
        React.createElement('button', { onClick: function() { setEditItem(null); }, style: styles.btnCancel }, 'Annuler')
      )
    )
  );
}

// ============================================
// PLATS
// ============================================
function renderPlats(plats, ingredients, composants, searchTerm, editItem, editType, setEditItem, setEditType, savePlat, deletePlat) {
  var filtered = plats.filter(function(p) {
    if (!searchTerm) return true;
    var s = searchTerm.toLowerCase();
    return (p.name || '').toLowerCase().includes(s) || (p.category || '').toLowerCase().includes(s);
  });

  var byCat = { 'Entrée': [], 'Plat': [], 'Dessert': [] };
  filtered.forEach(function(p) {
    var cat = p.category || 'Plat';
    if (!byCat[cat]) byCat[cat] = [];
    byCat[cat].push(p);
  });

  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 } },
      React.createElement('h3', { style: styles.sectionTitle }, filtered.length + ' plats'),
      React.createElement('button', {
        onClick: function() {
          setEditItem({ code: 'PLAT-NEW', name: '', category: 'Plat', sell_price: 0, portions_count: 1, instructions: '', ingredients: [], composants: [] });
          setEditType('plat');
        },
        style: styles.btnPrimary
      }, '+ Ajouter')
    ),
    editType === 'plat' && editItem && renderPlatForm(editItem, ingredients, composants, setEditItem, savePlat),
    Object.keys(byCat).map(function(cat) {
      if (byCat[cat].length === 0) return null;
      return React.createElement('div', { key: cat },
        React.createElement('h4', { style: { color: '#1e293b', borderBottom: '2px solid #e2e8f0', paddingBottom: 6, marginTop: 20, marginBottom: 12 } },
          (cat === 'Entrée' ? '🥗' : cat === 'Plat' ? '🥩' : '🍰') + ' ' + cat
        ),
        React.createElement('div', { style: styles.cardGrid },
          byCat[cat].map(function(p) {
            var ratio = parseFloat(p.ratio_percent) || 0;
            var ratioColor = ratio > 35 ? '#ef4444' : ratio > 30 ? '#f59e0b' : ratio > 25 ? '#3b82f6' : '#22c55e';
            return React.createElement('div', { key: p.id, style: styles.card },
              React.createElement('div', { style: styles.cardHeader },
                React.createElement('strong', null, p.name),
                React.createElement('div', { style: { display: 'flex', gap: 4 } },
                  React.createElement('button', {
                    onClick: function() { setEditItem(Object.assign({}, p)); setEditType('plat'); },
                    style: styles.btnEdit
                  }, '✏️'),
                  React.createElement('button', {
                    onClick: function() { deletePlat(p.id); },
                    style: styles.btnEdit
                  }, '🗑️')
                )
              ),
              React.createElement('div', { style: styles.cardBody },
                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
                  React.createElement('span', { style: { fontSize: 24, fontWeight: 700, color: ratioColor } }, ratio.toFixed(1) + '%'),
                  React.createElement('span', { style: { fontSize: 18, fontWeight: 600 } }, parseFloat(p.sell_price).toFixed(2) + '€')
                ),
                React.createElement('div', { style: { fontSize: 13, color: '#64748b', marginTop: 4 } },
                  'Coût: ' + parseFloat(p.food_cost_per_portion).toFixed(2) + '€ | Marge: ' + parseFloat(p.margin).toFixed(2) + '€'
                ),
                p.ingredients && p.ingredients.length > 0 && React.createElement('div', { style: { marginTop: 8, fontSize: 11, color: '#94a3b8', borderTop: '1px solid #f1f5f9', paddingTop: 6 } },
                  p.ingredients.map(function(i) {
                    return React.createElement('span', { key: i.id, style: { marginRight: 8 } }, i.ingredient_name);
                  }),
                  p.composants && p.composants.map(function(c) {
                    return React.createElement('span', { key: c.id, style: { marginRight: 8, fontStyle: 'italic', color: '#6366f1' } }, c.composant_name);
                  })
                )
              )
            );
          })
        )
      );
    })
  );
}

function renderPlatForm(item, allIngredients, allComposants, setEditItem, save) {
  function upd(field, value) {
    var updated = Object.assign({}, item);
    updated[field] = value;
    setEditItem(updated);
  }

  function addIngredient() {
    var ings = (item.ingredients || []).slice();
    ings.push({ ingredient_id: '', quantity: 0 });
    upd('ingredients', ings);
  }

  function addComposant() {
    var comps = (item.composants || []).slice();
    comps.push({ composant_id: '', quantity: 1 });
    upd('composants', comps);
  }

  function updateIngLine(index, field, value) {
    var ings = (item.ingredients || []).slice();
    ings[index] = Object.assign({}, ings[index]);
    ings[index][field] = value;
    upd('ingredients', ings);
  }

  function updateCompLine(index, field, value) {
    var comps = (item.composants || []).slice();
    comps[index] = Object.assign({}, comps[index]);
    comps[index][field] = value;
    upd('composants', comps);
  }

  function removeIngLine(index) {
    upd('ingredients', (item.ingredients || []).filter(function(_, i) { return i !== index; }));
  }

  function removeCompLine(index) {
    upd('composants', (item.composants || []).filter(function(_, i) { return i !== index; }));
  }

  return React.createElement('div', { style: styles.modal },
    React.createElement('div', { style: Object.assign({}, styles.modalContent, { maxWidth: 700 }) },
      React.createElement('h3', null, item.id ? 'Modifier plat' : 'Nouveau plat'),
      React.createElement('div', { style: { display: 'flex', gap: 10 } },
        React.createElement('div', { style: { flex: 1 } }, formRow('Code', 'code', item.code, function(v) { upd('code', v); })),
        React.createElement('div', { style: { flex: 2 } }, formRow('Nom', 'name', item.name, function(v) { upd('name', v); }))
      ),
      React.createElement('div', { style: { display: 'flex', gap: 10 } },
        React.createElement('div', { style: { flex: 1 } },
          React.createElement('label', { style: styles.label }, 'Catégorie'),
          React.createElement('select', {
            value: item.category || 'Plat',
            onChange: function(e) { upd('category', e.target.value); },
            style: styles.input
          },
            ['Entrée', 'Plat', 'Dessert'].map(function(c) {
              return React.createElement('option', { key: c, value: c }, c);
            })
          )
        ),
        React.createElement('div', { style: { flex: 1 } }, formRow('Prix de vente (€)', 'sell_price', item.sell_price, function(v) { upd('sell_price', parseFloat(v) || 0); }, 'number')),
        React.createElement('div', { style: { flex: 1 } }, formRow('Portions', 'portions_count', item.portions_count, function(v) { upd('portions_count', parseInt(v) || 1); }, 'number'))
      ),
      React.createElement('h4', { style: { marginTop: 15, borderTop: '1px solid #e2e8f0', paddingTop: 10 } }, '📦 Ingrédients directs'),
      (item.ingredients || []).map(function(ing, idx) {
        return React.createElement('div', { key: idx, style: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 } },
          React.createElement('select', {
            value: ing.ingredient_id || '',
            onChange: function(e) { updateIngLine(idx, 'ingredient_id', parseInt(e.target.value)); },
            style: Object.assign({}, styles.input, { flex: 2 })
          },
            React.createElement('option', { value: '' }, '-- Ingrédient --'),
            allIngredients.map(function(i) {
              return React.createElement('option', { key: i.id, value: i.id }, i.name + ' (' + i.unit + ')');
            })
          ),
          React.createElement('input', {
            type: 'number', step: '0.001',
            value: ing.quantity,
            onChange: function(e) { updateIngLine(idx, 'quantity', parseFloat(e.target.value) || 0); },
            placeholder: 'Qté',
            style: Object.assign({}, styles.input, { flex: 1 })
          }),
          React.createElement('button', { onClick: function() { removeIngLine(idx); }, style: styles.btnDelete }, '🗑️')
        );
      }),
      React.createElement('button', { onClick: addIngredient, style: Object.assign({}, styles.btnSmall, { marginTop: 5 }) }, '+ Ingrédient'),
      React.createElement('h4', { style: { marginTop: 15, borderTop: '1px solid #e2e8f0', paddingTop: 10 } }, '🥘 Composants'),
      (item.composants || []).map(function(comp, idx) {
        return React.createElement('div', { key: idx, style: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 } },
          React.createElement('select', {
            value: comp.composant_id || '',
            onChange: function(e) { updateCompLine(idx, 'composant_id', parseInt(e.target.value)); },
            style: Object.assign({}, styles.input, { flex: 2 })
          },
            React.createElement('option', { value: '' }, '-- Composant --'),
            allComposants.map(function(c) {
              return React.createElement('option', { key: c.id, value: c.id }, c.name + ' (' + parseFloat(c.cost_per_portion).toFixed(2) + '€/p)');
            })
          ),
          React.createElement('input', {
            type: 'number', step: '1',
            value: comp.quantity,
            onChange: function(e) { updateCompLine(idx, 'quantity', parseFloat(e.target.value) || 1); },
            placeholder: 'Nb portions',
            style: Object.assign({}, styles.input, { flex: 1 })
          }),
          React.createElement('button', { onClick: function() { removeCompLine(idx); }, style: styles.btnDelete }, '🗑️')
        );
      }),
      React.createElement('button', { onClick: addComposant, style: Object.assign({}, styles.btnSmall, { marginTop: 5 }) }, '+ Composant'),
      React.createElement('div', { style: { display: 'flex', gap: 10, marginTop: 20 } },
        React.createElement('button', { onClick: function() { save(item); }, style: styles.btnPrimary }, '💾 Enregistrer'),
        React.createElement('button', { onClick: function() { setEditItem(null); }, style: styles.btnCancel }, 'Annuler')
      )
    )
  );
}

// ============================================
// FORM HELPERS
// ============================================
function formRow(label, field, value, onChange, type) {
  return React.createElement('div', { style: { marginBottom: 8 } },
    React.createElement('label', { style: styles.label }, label),
    React.createElement('input', {
      type: type || 'text',
      step: type === 'number' ? '0.01' : undefined,
      value: value || '',
      onChange: function(e) { onChange(e.target.value); },
      style: styles.input
    })
  );
}

// ============================================
// STYLES
// ============================================
var styles = {
  loginContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' },
  loginCard: { background: 'white', padding: 40, borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.1)', textAlign: 'center', width: 350 },
  loginTitle: { fontSize: 28, margin: 0, color: '#1e293b' },
  loginSubtitle: { fontSize: 16, color: '#64748b', marginTop: 4, marginBottom: 24, fontWeight: 400 },
  app: { fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', maxWidth: 1200, margin: '0 auto', padding: 20, background: '#f8fafc', minHeight: '100vh' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  headerTitle: { fontSize: 22, color: '#1e293b', margin: 0 },
  headerActions: { display: 'flex', alignItems: 'center' },
  tabs: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e2e8f0', paddingBottom: 0 },
  tab: { padding: '10px 20px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: '#64748b', borderBottom: '2px solid transparent', marginBottom: -2, borderRadius: '8px 8px 0 0' },
  tabActive: { color: '#2563eb', borderBottomColor: '#2563eb', background: '#eff6ff' },
  searchBar: { marginBottom: 15 },
  searchInput: { width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  content: { background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  kpiRow: { display: 'flex', gap: 15, flexWrap: 'wrap', marginBottom: 20 },
  kpiCard: { flex: '1 1 200px', background: '#f8fafc', borderRadius: 12, padding: 20, textAlign: 'center', border: '1px solid #e2e8f0' },
  kpiValue: { fontSize: 28, fontWeight: 700, color: '#1e293b' },
  kpiLabel: { fontSize: 13, color: '#64748b', marginTop: 4 },
  section: { marginTop: 20 },
  sectionTitle: { fontSize: 16, color: '#1e293b', margin: '0 0 12px 0' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', background: '#f8fafc', color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '8px 10px' },
  tdRight: { padding: '8px 10px', textAlign: 'right' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 15 },
  card: { background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 15px', borderBottom: '1px solid #e2e8f0', background: '#f1f5f9' },
  cardBody: { padding: 15 },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 40, zIndex: 1000, overflow: 'auto' },
  modalContent: { background: 'white', borderRadius: 16, padding: 30, maxWidth: 550, width: '90%', maxHeight: '80vh', overflow: 'auto' },
  input: { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  label: { fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 3 },
  btnPrimary: { padding: '10px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  btnCancel: { padding: '10px 20px', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 500, fontSize: 14 },
  btnSmall: { padding: '6px 14px', background: '#f1f5f9', color: '#475569', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  btnEdit: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: '4px 8px' },
  btnDelete: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '4px' }
};

export default FoodCost;
